import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Users,
  MapPin,
  Calendar,
  FileText,
  User,
  X,
  LogOut,
  Sparkles,
  Plus,
  ChevronDown,
  BookOpen,
} from 'lucide-react';
import { Button } from './ui/button';
import { ProfileAvatar } from './ProfileAvatar';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from './ui/dropdown-menu';
import PaywallModal from './PaywallModal';

export const Navbar = ({ onStartOnboarding }) => {
  const { user, userType, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);

  useEffect(() => {
    document.body.classList.add('app-shell-active');
    return () => {
      document.body.classList.remove('app-shell-active');
    };
  }, []);

  const isActive = (path) => location.pathname === path;
  const getCreatePath = (basePath) => {
    if (location.pathname === basePath) {
      const params = new URLSearchParams(location.search);
      params.set('create', 'true');
      return `${basePath}?${params.toString()}`;
    }
    return `${basePath}?create=true`;
  };

  const navItems = [
    { path: '/lessons', label: 'Lezioni', icon: Calendar },
    { path: '/athletes', label: 'Atleti', icon: Users },
    { path: '/venues', label: 'Strutture', icon: MapPin },
    { path: '/reports', label: 'Report', icon: FileText },
    { path: '/profile', label: 'Profilo', icon: User },
  ];
  const desktopMenuItems = navItems.filter((item) => item.path !== '/profile');
  const mobileMenuItems = navItems.filter((item) => item.path === '/profile');

  const handleLogout = () => {
    setMobileMenuOpen(false);
    logout();
  };

  const handleStartOnboarding = () => {
    setMobileMenuOpen(false);
    if (onStartOnboarding) {
      onStartOnboarding();
    } else {
      navigate('/lessons?startOnboarding=true');
    }
  };

  const handleMobileAvatarClick = () => {
    if (userType === 'athlete') {
      navigate('/athlete/profile');
      return;
    }
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <nav
      className="bg-zinc-900 border-b border-white/10 backdrop-blur-xl fixed-bar fixed-top-bar w-full z-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-12 sm:h-14 md:h-16 md:py-1 navbar-row">
          <div className="flex items-center space-x-8 navbar-left">
            <Link to="/lessons" className="flex flex-col items-start leading-none navbar-logo">
              <span
                className="text-3xl font-black text-[#ccff00] tracking-tight"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                Trainers'
              </span>
              <span className="relative -top-0.5 text-sm sm:text-base font-extrabold text-white manager-word">
                MANAGER
              </span>
            </Link>

            {/* Desktop menu */}
            {userType !== 'athlete' && (
              <div className="hidden md:flex space-x-1 navbar-menu">
                {desktopMenuItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);

                  return (
                    <Link key={item.path} to={item.path}>
                      <Button
                        data-testid={`nav-${item.label.toLowerCase()}-btn`}
                        variant="ghost"
                        className={[
                          'flex items-center space-x-3 rounded-xl navbar-menu-button',
                          'px-3 py-2.5', // più alto
                          'text-base font-medium', // testo più grande
                          active
                            ? 'bg-[#ccff00]/10 text-[#ccff00] border border-[#ccff00]/30'
                            : 'text-zinc-400 hover:text-white hover:bg-zinc-800',
                        ].join(' ')}
                      >
                        {/* Icona più grande */}
                        <Icon className="w-5 h-5 navbar-icon" />
                        <span className="navbar-label">{item.label}</span>
                      </Button>
                    </Link>
                  );
                })}

                {/* Procedura guidata (iPad/Web) */}
                <Button
                  variant="ghost"
                  onClick={handleStartOnboarding}
                  className="flex items-center space-x-3 rounded-xl px-3 py-2.5 text-base font-medium text-zinc-400 hover:text-[#ccff00] hover:bg-zinc-800 navbar-menu-button"
                >
                  <Sparkles className="w-5 h-5 navbar-icon" />
                  <span className="navbar-label">Procedura Guidata</span>
                </Button>

                {/* Istruzioni e supporto (iPad/Web) */}
                <Button
                  variant="ghost"
                  asChild
                  className="flex items-center space-x-3 rounded-xl px-3 py-2.5 text-base font-medium text-zinc-400 hover:text-[#ccff00] hover:bg-zinc-800 navbar-menu-button"
                >
                  <a href="https://trainersmanager.com/guida.html" target="_blank" rel="noopener noreferrer">
                    <BookOpen className="w-4 h-4 navbar-icon" />
                    <span className="navbar-label">Istruzioni e supporto</span>
                  </a>
                </Button>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-4 navbar-right">
            {userType !== 'athlete' && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="hidden 2xl:inline-flex items-center gap-2 rounded-xl px-3 py-2.5 text-base font-medium text-zinc-200 hover:text-white hover:bg-zinc-800"
                    data-testid="desktop-create-menu"
                  >
                    <Plus className="w-4 h-4" />
                    Nuovo
                    <ChevronDown className="w-4 h-4 text-zinc-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={8} className="bg-zinc-900 border border-white/10">
                  <DropdownMenuItem
                    className="cursor-pointer focus:bg-zinc-800 focus:text-white"
                    onClick={() => navigate(getCreatePath('/lessons'))}
                  >
                    Nuova lezione
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer focus:bg-zinc-800 focus:text-white"
                    onClick={() => navigate(getCreatePath('/athletes'))}
                  >
                    Nuovo atleta
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer focus:bg-zinc-800 focus:text-white"
                    onClick={() => navigate(getCreatePath('/venues'))}
                  >
                    Nuova struttura
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Link
              to={userType === 'athlete' ? '/athlete/profile' : '/profile'}
              className="hidden md:inline-flex relative focus:outline-none focus:ring-2 focus:ring-[#ccff00] rounded-full"
              data-testid="desktop-profile-avatar"
            >
              <ProfileAvatar
                src={user?.profile_photo}
                name={user?.name}
                size="md"
                className="border-2 border-transparent hover:border-[#ccff00] transition-colors"
              />
            </Link>

            {/* Mobile: Avatar button che apre il menu */}
            <button
              onClick={handleMobileAvatarClick}
              className="md:hidden relative focus:outline-none focus:ring-2 focus:ring-[#ccff00] rounded-full"
              data-testid="mobile-menu-avatar"
            >
              <ProfileAvatar
                src={user?.profile_photo}
                name={user?.name}
                size="md"
                className="border-2 border-transparent hover:border-[#ccff00] transition-colors"
              />
              {mobileMenuOpen && userType !== 'athlete' && (
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-zinc-800 rounded-full flex items-center justify-center border border-zinc-700">
                  <X className="w-3.5 h-3.5 text-white" />
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && userType !== 'athlete' && (
          <div className="md:hidden pb-4 border-t border-white/10 mt-2 pt-4">
            {/* User info header */}
            <div className="flex items-center space-x-3 px-2 pb-4 mb-3 border-b border-white/10">
              <ProfileAvatar src={user?.profile_photo} name={user?.name} size="lg" />
              <div>
                <div className="text-white font-medium">{user?.name}</div>
                <div className="text-sm text-zinc-400">
                  {userType === 'coach' ? 'Allenatore' : 'Atleta'}
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button
                      variant="ghost"
                      className={[
                        'w-full flex items-center justify-start space-x-3 rounded-xl',
                        'h-12 px-3',
                        'text-base font-medium',
                        active
                          ? 'bg-[#ccff00]/10 text-[#ccff00] border border-[#ccff00]/30'
                          : 'text-zinc-400 hover:text-white hover:bg-zinc-800',
                      ].join(' ')}
                    >
                      <Icon className="w-5 h-5 shrink-0" />
                      <span>{item.label}</span>
                    </Button>
                  </Link>
                );
              })}

              {/* Divider */}
              <div className="border-t border-white/10 my-2" />

              {/* Procedura guidata */}
              <Button
                variant="ghost"
                onClick={handleStartOnboarding}
                className="w-full flex items-center justify-start space-x-3 rounded-xl h-12 px-3 text-base font-medium text-zinc-400 hover:text-[#ccff00] hover:bg-zinc-800"
              >
                <Sparkles className="w-5 h-5 shrink-0" />
                <span>Procedura Guidata</span>
              </Button>

              {/* Istruzioni e supporto */}
              <Button
                variant="ghost"
                asChild
                className="w-full flex items-center justify-start space-x-3 rounded-xl h-12 px-3 text-base font-medium text-zinc-400 hover:text-[#ccff00] hover:bg-zinc-800"
              >
                <a
                  href="https://trainersmanager.com/guida.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <BookOpen className="w-5 h-5 shrink-0" />
                  <span>Istruzioni e supporto</span>
                </a>
              </Button>

              {/* Divider */}
              <div className="border-t border-white/10 my-2" />

              {/* Logout */}
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="w-full flex items-center justify-start space-x-3 rounded-xl h-12 px-3 text-base font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <LogOut className="w-5 h-5 shrink-0" />
                <span>Esci</span>
              </Button>
            </div>
          </div>
        )}
      </div>

      <PaywallModal open={showPaywall} onOpenChange={setShowPaywall} />
    </nav>
  );
};
